import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFeedStore } from '@/store/feed.store'
import type { Language } from '@olimhub/shared'

const HERO_LANGS: Language[] = ['en', 'ru', 'pt', 'es']
const SUPPORTED_LANG_CODES = ['en', 'ru', 'pt', 'es']

export function HeroBand() {
  const { t, i18n } = useTranslation()
  const { guestFeedLanguages, setGuestFeedLanguages } = useFeedStore()
  const selectedLang = guestFeedLanguages[0] ?? null

  function handleLangClick(lang: Language) {
    if (selectedLang === lang) {
      setGuestFeedLanguages([])
      const browser = navigator.language.slice(0, 2).toLowerCase()
      const reverted = SUPPORTED_LANG_CODES.includes(browser) ? browser : 'en'
      void i18n.changeLanguage(reverted)
      localStorage.removeItem('i18nextLng')
      document.documentElement.lang = reverted
    } else {
      setGuestFeedLanguages([lang])
      void i18n.changeLanguage(lang)
      localStorage.setItem('i18nextLng', lang)
      document.documentElement.lang = lang
    }
  }

  return (
    <div style={{ padding: '6px 20px 22px', textAlign: 'center', position: 'relative' }}>
      {/* Mobile-only gradient — on desktop the Layout-level gradient shows through */}
      <div
        className="lg:hidden"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, color-mix(in srgb, var(--brand) 17%, var(--bg)) 0%, color-mix(in srgb, var(--brand) 5%, var(--bg)) 52%, var(--bg-sunk) 100%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Spectrum bar */}
        <div
          className="oh-spectrum oh-spectrum--smooth"
          style={{ width: 96, height: 5, margin: '0 auto 16px' }}
        />

        {/* Tagline */}
        <h1
          style={{
            fontSize: 'var(--fs-h1)',
            fontWeight: 800,
            color: 'var(--text-strong)',
            letterSpacing: '-0.02em',
            lineHeight: 1.12,
            margin: 0,
          }}
        >
          {t('welcome.tagline')}
        </h1>

        {/* Blurb */}
        <p
          style={{
            margin: '10px auto 18px',
            maxWidth: '30ch',
            color: 'var(--text-body)',
            fontSize: 'var(--fs-body)',
            lineHeight: 'var(--lh-normal)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {t('welcome.blurb')}
        </p>

        {/* Button row */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 18 }}>
          <Link
            to="/signup"
            style={{
              flex: 1,
              maxWidth: 180,
              minHeight: 'var(--tap-min, 44px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--brand)',
              color: 'var(--on-brand)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-body)',
              borderRadius: 'var(--radius-pill)',
              boxShadow: 'var(--shadow-card)',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 20px',
            }}
          >
            {t('welcome.signup')}
          </Link>

          <Link
            to="/login"
            style={{
              flex: 1,
              maxWidth: 140,
              minHeight: 'var(--tap-min, 44px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              color: 'var(--text-strong)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-body)',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)',
              textDecoration: 'none',
              cursor: 'pointer',
              padding: '0 20px',
            }}
          >
            {t('welcome.signin')}
          </Link>
        </div>

        {/* Feed language filter — single-select, also switches UI language */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <div
            role="group"
            aria-label="Feed language"
            style={{
              display: 'inline-flex',
              padding: 3,
              gap: 2,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            {HERO_LANGS.map(lang => {
              const active = selectedLang === lang
              return (
                <button
                  key={lang}
                  onClick={() => handleLangClick(lang)}
                  aria-pressed={active}
                  style={{
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-pill)',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 'var(--fs-xs)',
                    letterSpacing: '0.02em',
                    background: active ? 'var(--brand)' : 'transparent',
                    color: active ? 'var(--on-brand)' : 'var(--text-muted)',
                    transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              )
            })}
          </div>
        </div>

        {/* Static page links */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { to: '/about', label: t('nav.about') },
            { to: '/conduct', label: t('nav.conduct') },
            { to: '/privacy', label: t('nav.privacy') },
          ].map(({ to, label }, i) => (
            <span key={to} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}>·</span>}
              <Link
                to={to}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-muted)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                {label}
              </Link>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
